import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  ChevronRight,
  Download,
  ExternalLink,
  LogOut,
  RefreshCw,
  Search,
  Loader2,
  DollarSign,
  Package,
  Trophy,
  Truck,
  Pencil,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  adminOrdersCsvUrl,
  fetchAdminDashboard,
  fetchAdminOrders,
  patchOrderStatus,
  retryEasyshipShipment,
  editOrder,
} from "@/lib/api";
import { KNOW_IT_OWLS_LOGO } from "@/data/products";

/* Allowed status values shown in dropdown. Order = canonical lifecycle. */
const STATUSES = [
  "awaiting_payment",
  "paid",
  "packed",
  "shipped",
  "delivered",
  "cancelled",
  "refunded",
];

const STATUS_COLORS = {
  awaiting_payment: { bg: "var(--st-muted)", fg: "var(--st-fg)" },
  paid: { bg: "var(--st-mustard)", fg: "var(--st-fg)" },
  packed: { bg: "var(--st-orange)", fg: "#fff" },
  shipped: { bg: "var(--st-gold)", fg: "var(--st-fg)" },
  delivered: { bg: "var(--st-maroon)", fg: "#fff" },
  cancelled: { bg: "#1a1410", fg: "var(--st-gold)" },
  refunded: { bg: "#1a1410", fg: "var(--st-mustard)" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (_) {
    return iso;
  }
};

const fmtMoney = (n, c = "usd") =>
  `${(c || "usd").toUpperCase()} $${Number(n || 0).toFixed(2)}`;

export default function AdminOrders() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const limit = 25;
  const [filters, setFilters] = useState({ status: "", q: "", frm: "", to: "" });
  const [expanded, setExpanded] = useState(null);
  const [updating, setUpdating] = useState(null);
  const [retryingShipment, setRetryingShipment] = useState(null);
  const [dashboard, setDashboard] = useState(null);

  const reloadDashboard = async () => {
    try {
      const d = await fetchAdminDashboard();
      setDashboard(d);
    } catch (_) {
      // non-blocking
    }
  };

  useEffect(() => {
    reloadDashboard();
  }, []);

  const params = useMemo(() => {
    const p = { page, limit };
    if (filters.status) p.status = filters.status;
    if (filters.q) p.q = filters.q;
    if (filters.frm) p.frm = filters.frm;
    if (filters.to) p.to = filters.to;
    return p;
  }, [page, filters]);

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchAdminOrders(params);
      setOrders(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error("Could not load orders.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [params]);

  const handleStatus = async (orderId, newStatus) => {
    setUpdating(orderId);
    try {
      await patchOrderStatus(orderId, newStatus);
      toast.success(`Status → ${newStatus}`);
      setOrders((curr) =>
        curr.map((o) => (o.id === orderId ? { ...o, status: newStatus } : o))
      );
      reloadDashboard();
    } catch (e) {
      toast.error("Could not update status.");
    } finally {
      setUpdating(null);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const retryShipment = async (orderId) => {
    setRetryingShipment(orderId);
    try {
      await retryEasyshipShipment(orderId);
      toast.success("Shipment retry queued. Refreshing in a few seconds…");
      setTimeout(load, 3500);
    } catch (e) {
      toast.error("Could not retry shipment.");
    } finally {
      setTimeout(() => setRetryingShipment(null), 3500);
    }
  };

  const [editingOrder, setEditingOrder] = useState(null);
  const openEdit = (order) => setEditingOrder(order);
  const closeEdit = () => setEditingOrder(null);
  const onEditSaved = (updated) => {
    setOrders((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
    setEditingOrder(null);
    toast.success("Order updated. You can now retry the shipment.");
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const updateFilter = (k, v) => {
    setPage(1);
    setFilters((f) => ({ ...f, [k]: v }));
  };

  const clearFilters = () => {
    setPage(1);
    setFilters({ status: "", q: "", frm: "", to: "" });
  };

  return (
    <div className="min-h-screen bg-[var(--st-bg)]" data-testid="admin-orders-page">
      {/* HEADER */}
      <header className="border-b-2 border-[#111] bg-[var(--st-fg)] text-[var(--st-cream)]">
        <div className="max-w-[1500px] mx-auto px-4 md:px-6 py-3 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2 mr-auto">
            <img src={KNOW_IT_OWLS_LOGO} alt="" className="h-9" />
            <div className="leading-tight">
              <p className="font-display uppercase tracking-tight text-lg text-[var(--st-mustard)]">
                Substitute Teacher
              </p>
              <p className="font-heading uppercase tracking-[0.22em] text-[10px] text-[var(--st-cream)]/70">
                Order Console
              </p>
            </div>
          </Link>
          <span className="font-heading uppercase tracking-[0.22em] text-[10px] text-[var(--st-cream)]/60 hidden md:inline">
            {user?.email}
          </span>
          <button
            data-testid="admin-logout"
            onClick={handleLogout}
            className="inline-flex items-center gap-1.5 border-2 border-[var(--st-cream)] px-3 py-1.5 font-heading uppercase tracking-[0.18em] text-xs text-[var(--st-cream)] hover:bg-[var(--st-mustard)] hover:text-[var(--st-fg)] hover:border-[var(--st-fg)] transition-colors"
          >
            <LogOut size={12} /> Logout
          </button>
        </div>
      </header>

      {/* DASHBOARD TILES — instant ops pulse for the WoE team */}
      <DashboardTiles data={dashboard} />

      {/* FILTERS */}
      <section className="max-w-[1500px] mx-auto px-4 md:px-6 py-4">
        <div className="border-2 border-[#111] bg-[var(--st-cream)] shadow-brutal-sm p-3 md:p-4 grid grid-cols-12 gap-3 items-end">
          {/* Search */}
          <div className="col-span-12 md:col-span-4">
            <label className="font-heading uppercase tracking-[0.22em] text-[10px] text-[var(--st-fg)]/70">
              Search
            </label>
            <div className="mt-1 flex items-center border-2 border-[#111] bg-white px-2">
              <Search size={14} className="text-[var(--st-fg)]/60" />
              <input
                type="text"
                value={filters.q}
                onChange={(e) => updateFilter("q", e.target.value)}
                data-testid="admin-filter-search"
                placeholder="Order # or email"
                className="flex-1 px-2 py-2 font-body text-sm outline-none bg-transparent"
              />
            </div>
          </div>

          {/* Status */}
          <div className="col-span-6 md:col-span-2">
            <label className="font-heading uppercase tracking-[0.22em] text-[10px] text-[var(--st-fg)]/70">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => updateFilter("status", e.target.value)}
              data-testid="admin-filter-status"
              className="mt-1 w-full border-2 border-[#111] bg-white px-2 py-2 font-body text-sm outline-none"
            >
              <option value="">All</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s.replace("_", " ")}
                </option>
              ))}
            </select>
          </div>

          {/* From */}
          <div className="col-span-6 md:col-span-2">
            <label className="font-heading uppercase tracking-[0.22em] text-[10px] text-[var(--st-fg)]/70">
              From
            </label>
            <input
              type="date"
              value={filters.frm}
              onChange={(e) => updateFilter("frm", e.target.value)}
              data-testid="admin-filter-from"
              className="mt-1 w-full border-2 border-[#111] bg-white px-2 py-2 font-body text-sm outline-none"
            />
          </div>

          {/* To */}
          <div className="col-span-6 md:col-span-2">
            <label className="font-heading uppercase tracking-[0.22em] text-[10px] text-[var(--st-fg)]/70">
              To
            </label>
            <input
              type="date"
              value={filters.to}
              onChange={(e) => updateFilter("to", e.target.value)}
              data-testid="admin-filter-to"
              className="mt-1 w-full border-2 border-[#111] bg-white px-2 py-2 font-body text-sm outline-none"
            />
          </div>

          <div className="col-span-6 md:col-span-2 flex gap-2">
            <button
              onClick={clearFilters}
              data-testid="admin-clear-filters"
              className="flex-1 border-2 border-[#111] bg-[var(--st-bg)] hover:bg-[var(--st-muted)] font-heading uppercase tracking-[0.18em] text-xs py-2"
            >
              Clear
            </button>
            <button
              onClick={load}
              data-testid="admin-refresh"
              className="border-2 border-[#111] bg-[var(--st-mustard)] hover:bg-[var(--st-gold)] px-3 py-2"
              title="Refresh"
            >
              <RefreshCw size={14} />
            </button>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
          <p className="font-heading uppercase tracking-[0.22em] text-[11px] text-[var(--st-fg)]/70">
            {loading ? "Loading…" : `${total} order${total === 1 ? "" : "s"}`}
          </p>
          <a
            href={adminOrdersCsvUrl({
              ...(filters.status ? { status: filters.status } : {}),
              ...(filters.q ? { q: filters.q } : {}),
              ...(filters.frm ? { frm: filters.frm } : {}),
              ...(filters.to ? { to: filters.to } : {}),
            })}
            data-testid="admin-export-csv"
            className="inline-flex items-center gap-1.5 border-2 border-[#111] bg-[var(--st-maroon)] text-white hover:bg-[var(--st-mustard)] hover:text-[var(--st-fg)] px-3 py-1.5 font-heading uppercase tracking-[0.18em] text-xs"
          >
            <Download size={12} /> Export CSV
          </a>
        </div>
      </section>

      {/* TABLE */}
      <section className="max-w-[1500px] mx-auto px-4 md:px-6 pb-12">
        <div className="border-2 border-[#111] bg-[var(--st-cream)] shadow-brutal overflow-hidden">
          {/* Column heads (desktop only) */}
          <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-2 border-b-2 border-[#111] bg-[var(--st-mustard)] font-heading uppercase tracking-[0.22em] text-[10px] text-[var(--st-fg)]">
            <div className="col-span-2">Order #</div>
            <div className="col-span-3">Customer</div>
            <div className="col-span-2">Date</div>
            <div className="col-span-2">Total</div>
            <div className="col-span-3">Status</div>
          </div>

          {loading && orders.length === 0 && (
            <div className="py-16 text-center font-heading uppercase tracking-[0.22em] text-xs text-[var(--st-fg)]/60">
              <Loader2 size={16} className="inline animate-spin mr-2" />
              Loading orders…
            </div>
          )}

          {!loading && orders.length === 0 && (
            <div className="py-16 text-center font-heading uppercase tracking-[0.22em] text-xs text-[var(--st-fg)]/60">
              No orders match these filters.
            </div>
          )}

          <ul className="divide-y-2 divide-[#11111122]">
            {orders.map((o) => {
              const isExpanded = expanded === o.id;
              const cust = o.customer || {};
              const ship = o.shipping_address || {};
              const bill = o.billing_address || ship;
              const color = STATUS_COLORS[o.status] || STATUS_COLORS.awaiting_payment;
              return (
                <li key={o.id} data-testid={`admin-order-${o.id}`}>
                  <button
                    type="button"
                    onClick={() => setExpanded((c) => (c === o.id ? null : o.id))}
                    className="w-full grid grid-cols-12 gap-2 px-4 py-3 items-center text-left hover:bg-[var(--st-bg)] transition-colors"
                  >
                    <div className="col-span-12 md:col-span-2 flex items-center gap-2">
                      {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      <span className="font-display text-sm">{o.order_number}</span>
                    </div>
                    <div className="col-span-12 md:col-span-3 font-body text-sm">
                      <p className="font-display uppercase tracking-tight text-sm leading-none">
                        {cust.first_name} {cust.last_name}
                      </p>
                      <p className="text-[12px] text-[var(--st-fg)]/60 truncate">{cust.email}</p>
                    </div>
                    <div className="col-span-6 md:col-span-2 font-body text-[12px] text-[var(--st-fg)]/70">
                      {fmtDate(o.created_at)}
                    </div>
                    <div className="col-span-6 md:col-span-2 font-display text-base">
                      {fmtMoney(o.total, o.currency)}
                    </div>
                    <div className="col-span-12 md:col-span-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-2">
                        <span
                          data-testid={`admin-order-status-${o.id}`}
                          className="inline-flex items-center justify-center border-2 border-[#111] px-2 py-0.5 font-heading uppercase tracking-[0.18em] text-[10px] whitespace-nowrap"
                          style={{ background: color.bg, color: color.fg }}
                        >
                          {o.status?.replace("_", " ")}
                        </span>
                        <select
                          value={o.status}
                          onChange={(e) => handleStatus(o.id, e.target.value)}
                          disabled={updating === o.id}
                          data-testid={`admin-order-status-select-${o.id}`}
                          className="border-2 border-[#111] bg-white text-xs px-1 py-0.5 outline-none disabled:opacity-50"
                        >
                          {STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {s.replace("_", " ")}
                            </option>
                          ))}
                        </select>
                        {updating === o.id && (
                          <Loader2 size={12} className="animate-spin text-[var(--st-fg)]/40" />
                        )}
                      </div>
                    </div>
                  </button>

                  <AnimatePresence initial={false}>
                    {isExpanded && (
                      <motion.div
                        key="details"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden bg-[var(--st-bg)] border-t-2 border-[#11111122]"
                      >
                        <div className="grid grid-cols-12 gap-4 p-4 md:p-5">
                          {/* Items */}
                          <div className="col-span-12 md:col-span-5">
                            <h3 className="font-heading uppercase tracking-[0.22em] text-[10px] text-[var(--st-fg)]/60 mb-2">
                              Items
                            </h3>
                            <ul className="border-2 border-[#111] bg-[var(--st-cream)] divide-y-2 divide-[#11111122]">
                              {(o.items || []).map((it, i) => (
                                <li key={i} className="flex gap-3 p-2 items-center">
                                  {it.image && (
                                    <img
                                      src={it.image}
                                      alt=""
                                      className="w-12 h-14 object-cover border-2 border-[#111]"
                                    />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className="font-display uppercase text-sm leading-tight flex items-center gap-2 flex-wrap">
                                      <span>{it.name}</span>
                                      {it.size && (
                                        <span
                                          className="inline-block bg-[var(--st-maroon)] text-white border-2 border-[#111] px-1.5 py-0.5 font-heading text-[10px] tracking-[0.18em]"
                                          data-testid={`admin-item-size-${o.id}-${i}`}
                                        >
                                          SIZE {it.size}
                                        </span>
                                      )}
                                    </p>
                                    <p className="font-heading uppercase tracking-[0.18em] text-[10px] text-[var(--st-fg)]/60">
                                      ${Number(it.unit_price).toFixed(2)} × {it.quantity}
                                    </p>
                                  </div>
                                  <p className="font-display text-sm">
                                    ${Number(it.line_total).toFixed(2)}
                                  </p>
                                </li>
                              ))}
                            </ul>
                            <div className="mt-2 text-right space-y-0.5 font-body text-xs">
                              <p>Subtotal: ${Number(o.subtotal || 0).toFixed(2)}</p>
                              <p>
                                Shipping:{" "}
                                {Number(o.shipping_fee || 0) === 0
                                  ? "Free"
                                  : `$${Number(o.shipping_fee).toFixed(2)}`}
                              </p>
                              <p>Tax: ${Number(o.tax || 0).toFixed(2)}</p>
                              <p className="font-display text-base text-[var(--st-maroon)]">
                                Total: ${Number(o.total || 0).toFixed(2)}
                              </p>
                            </div>
                          </div>

                          {/* Shipping */}
                          <div className="col-span-12 md:col-span-3">
                            <h3 className="font-heading uppercase tracking-[0.22em] text-[10px] text-[var(--st-fg)]/60 mb-2">
                              Shipping to
                            </h3>
                            <address className="not-italic font-body text-sm leading-snug border-2 border-[#111] bg-[var(--st-cream)] p-3">
                              <p className="font-display uppercase text-sm">
                                {cust.first_name} {cust.last_name}
                              </p>
                              <p>{ship.line1}</p>
                              {ship.line2 && <p>{ship.line2}</p>}
                              <p>
                                {ship.city}, {ship.state} {ship.postal_code}
                              </p>
                              <p>{ship.country}</p>
                              {cust.phone && (
                                <p className="mt-1 text-[var(--st-fg)]/60">{cust.phone}</p>
                              )}
                              <p className="mt-1 text-[var(--st-fg)]/60 break-all">{cust.email}</p>
                            </address>
                          </div>

                          {/* Billing + notes */}
                          <div className="col-span-12 md:col-span-4">
                            <h3 className="font-heading uppercase tracking-[0.22em] text-[10px] text-[var(--st-fg)]/60 mb-2">
                              Billing
                              {o.billing_same_as_shipping && (
                                <span className="ml-2 text-[var(--st-maroon)]">(same as shipping)</span>
                              )}
                            </h3>
                            <address className="not-italic font-body text-sm leading-snug border-2 border-[#111] bg-[var(--st-cream)] p-3">
                              <p>{bill.line1}</p>
                              {bill.line2 && <p>{bill.line2}</p>}
                              <p>
                                {bill.city}, {bill.state} {bill.postal_code}
                              </p>
                              <p>{bill.country}</p>
                            </address>

                            {o.notes && (
                              <>
                                <h3 className="mt-3 font-heading uppercase tracking-[0.22em] text-[10px] text-[var(--st-fg)]/60 mb-2">
                                  Notes
                                </h3>
                                <p className="font-body text-sm border-2 border-[#111] bg-[var(--st-cream)] p-3">
                                  {o.notes}
                                </p>
                              </>
                            )}

                            {Array.isArray(o.status_history) && o.status_history.length > 0 && (
                              <>
                                <h3 className="mt-3 font-heading uppercase tracking-[0.22em] text-[10px] text-[var(--st-fg)]/60 mb-2">
                                  Status history
                                </h3>
                                <ul className="border-2 border-[#111] bg-[var(--st-cream)] divide-y-2 divide-[#11111122]">
                                  {o.status_history.map((h, i) => (
                                    <li key={i} className="px-2 py-1.5 flex items-center justify-between font-body text-[11px]">
                                      <span className="font-heading uppercase tracking-[0.18em]">
                                        {h.status?.replace("_", " ")}
                                      </span>
                                      <span className="text-[var(--st-fg)]/60">
                                        {fmtDate(h.at)}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              </>
                            )}

                            <ShipmentBlock order={o} onRetry={() => retryShipment(o.id)} retrying={retryingShipment === o.id} />

                            <button
                              onClick={() => openEdit(o)}
                              data-testid={`admin-order-edit-${o.id}`}
                              className="mt-3 inline-flex items-center gap-1.5 font-heading uppercase tracking-[0.18em] text-[11px] border-2 border-[#111] bg-white hover:bg-[var(--st-mustard)] px-3 py-2 shadow-brutal-sm"
                            >
                              <Pencil size={12} /> Edit customer / address
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </li>
              );
            })}
          </ul>
        </div>

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-center gap-3 font-heading uppercase tracking-[0.22em] text-[11px]">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              data-testid="admin-page-prev"
              className="border-2 border-[#111] bg-[var(--st-cream)] hover:bg-[var(--st-mustard)] px-3 py-1.5 disabled:opacity-40"
            >
              ← Prev
            </button>
            <span data-testid="admin-page-info">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              data-testid="admin-page-next"
              className="border-2 border-[#111] bg-[var(--st-cream)] hover:bg-[var(--st-mustard)] px-3 py-1.5 disabled:opacity-40"
            >
              Next →
            </button>
          </div>
        )}
      </section>
      {editingOrder && (
        <EditOrderModal
          order={editingOrder}
          onClose={closeEdit}
          onSaved={onEditSaved}
        />
      )}
    </div>
  );
}

/* ─── Easyship Shipment Block — appears in each expanded order row ─── */
function ShipmentBlock({ order, onRetry, retrying }) {
  const es = order.easyship || {};
  const hasShipment = !!es.shipment_id;
  const hasError = es.status === "error";

  return (
    <div className="mt-3" data-testid={`shipment-block-${order.id}`}>
      <h3 className="font-heading uppercase tracking-[0.22em] text-[10px] text-[var(--st-fg)]/60 mb-2 flex items-center gap-2">
        <Truck size={11} /> Shipment
      </h3>
      <div className="border-2 border-[#111] bg-[var(--st-cream)] p-3 space-y-2">
        {!hasShipment && !hasError && (
          <p className="font-body text-xs text-[var(--st-fg)]/60">
            No shipment created yet. It will appear here once payment clears.
          </p>
        )}
        {hasError && (
          <div className="font-body text-xs text-[var(--st-maroon)]">
            <p className="font-display uppercase">Shipment failed</p>
            <p className="mt-1 text-[var(--st-fg)]/80 whitespace-pre-wrap break-words">
              {es.last_error?.message || "Unknown error"}
            </p>
            {/* Full field-level error payload from Easyship (collapsed by
                default). Populated on 4xx responses when Easyship returns
                an `errors` array. Invaluable for debugging 422s. */}
            {es.last_error?.payload && (
              <details className="mt-2 text-[var(--st-fg)]/70">
                <summary
                  className="cursor-pointer font-heading uppercase tracking-[0.18em] text-[10px]"
                  data-testid={`shipment-error-details-toggle-${order.id}`}
                >
                  Show error payload
                </summary>
                <pre
                  className="mt-1 max-h-56 overflow-auto bg-white border-2 border-[#111] p-2 text-[10px] leading-snug"
                  data-testid={`shipment-error-payload-${order.id}`}
                >
{JSON.stringify(es.last_error.payload, null, 2)}
                </pre>
              </details>
            )}
          </div>
        )}
        {hasShipment && (
          <>
            <p className="font-body text-xs">
              <span className="text-[var(--st-fg)]/60">ID:</span>{" "}
              <span className="font-mono">{es.shipment_id}</span>
            </p>
            {es.tracking_number && (
              <p className="font-body text-xs">
                <span className="text-[var(--st-fg)]/60">Tracking #:</span>{" "}
                <span className="font-mono">{es.tracking_number}</span>
              </p>
            )}
            {es.status && (
              <p className="font-body text-xs">
                <span className="text-[var(--st-fg)]/60">Easyship status:</span>{" "}
                <span className="font-heading uppercase tracking-[0.18em]">
                  {es.status?.replace("_", " ")}
                </span>
              </p>
            )}
          </>
        )}
        <div className="flex items-center gap-2 pt-1 flex-wrap">
          {es.tracking_url && (
            <a
              href={es.tracking_url}
              target="_blank"
              rel="noreferrer"
              data-testid={`shipment-track-link-${order.id}`}
              className="inline-flex items-center gap-1 border-2 border-[#111] bg-[var(--st-mustard)] hover:bg-[var(--st-gold)] px-2.5 py-1 font-heading uppercase tracking-[0.18em] text-[10px]"
            >
              Track <ExternalLink size={10} />
            </a>
          )}
          <button
            type="button"
            onClick={onRetry}
            disabled={retrying}
            data-testid={`shipment-retry-${order.id}`}
            className="inline-flex items-center gap-1 border-2 border-[#111] bg-[var(--st-cream)] hover:bg-[var(--st-maroon)] hover:text-white px-2.5 py-1 font-heading uppercase tracking-[0.18em] text-[10px] disabled:opacity-50"
          >
            {retrying ? (
              <>
                <Loader2 size={10} className="animate-spin" /> Retrying
              </>
            ) : hasShipment ? (
              <>Re-sync</>
            ) : (
              <>Retry</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Dashboard Tiles — last-7-days revenue · fulfillment queue · top sellers ── */function DashboardTiles({ data }) {
  const fmtUsd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const isLoading = !data;

  return (
    <section className="max-w-[1500px] mx-auto px-4 md:px-6 pt-4">
      <div className="grid grid-cols-12 gap-3 md:gap-4">
        {/* Revenue */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          data-testid="dashboard-tile-revenue"
          className="col-span-12 md:col-span-4 border-2 border-[#111] bg-[var(--st-mustard)] shadow-brutal-sm p-4 flex items-center gap-4"
        >
          <div className="w-12 h-12 flex items-center justify-center border-2 border-[#111] bg-[var(--st-fg)] text-[var(--st-mustard)] shrink-0">
            <DollarSign size={20} strokeWidth={3} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-heading uppercase tracking-[0.22em] text-[10px] text-[var(--st-fg)]/70">
              Last 7 days
            </p>
            <p
              className="font-display text-3xl md:text-4xl text-[var(--st-fg)] leading-none"
              data-testid="dashboard-week-revenue"
            >
              {isLoading ? "—" : fmtUsd(data.week_revenue)}
            </p>
            <p className="font-body text-xs text-[var(--st-fg)]/70 mt-1">
              {isLoading ? "…" : `${data.week_orders} order${data.week_orders === 1 ? "" : "s"}`}
            </p>
          </div>
        </motion.div>

        {/* Fulfillment queue */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.05 }}
          data-testid="dashboard-tile-fulfillment"
          className="col-span-12 md:col-span-4 border-2 border-[#111] bg-[var(--st-maroon)] text-[var(--st-cream)] shadow-brutal-sm p-4 flex items-center gap-4"
        >
          <div className="w-12 h-12 flex items-center justify-center border-2 border-[var(--st-cream)] bg-[var(--st-cream)] text-[var(--st-maroon)] shrink-0">
            <Package size={20} strokeWidth={3} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-heading uppercase tracking-[0.22em] text-[10px] text-[var(--st-cream)]/80">
              Awaiting Fulfillment
            </p>
            <p
              className="font-display text-3xl md:text-4xl leading-none"
              data-testid="dashboard-fulfillment-count"
            >
              {isLoading ? "—" : data.fulfillment_count}
            </p>
            <p className="font-body text-xs text-[var(--st-cream)]/80 mt-1">
              paid + packed
            </p>
          </div>
        </motion.div>

        {/* Top sellers */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.1 }}
          data-testid="dashboard-tile-best-sellers"
          className="col-span-12 md:col-span-4 border-2 border-[#111] bg-[var(--st-cream)] shadow-brutal-sm p-4"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 flex items-center justify-center border-2 border-[#111] bg-[var(--st-gold)] text-[var(--st-fg)] shrink-0">
              <Trophy size={14} strokeWidth={3} />
            </div>
            <p className="font-heading uppercase tracking-[0.22em] text-[10px] text-[var(--st-fg)]/70">
              Top Sellers
            </p>
          </div>
          {isLoading ? (
            <p className="font-body text-sm text-[var(--st-fg)]/60">Loading…</p>
          ) : data.best_sellers.length === 0 ? (
            <p className="font-body text-sm text-[var(--st-fg)]/60">No sales yet.</p>
          ) : (
            <ul className="space-y-1.5">
              {data.best_sellers.map((b, i) => (
                <li
                  key={b.product_id}
                  data-testid={`dashboard-best-seller-${i}`}
                  className="flex items-center gap-2"
                >
                  <span className="font-pixel text-sm text-[var(--st-maroon)] w-5">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {b.image && (
                    <img
                      src={b.image}
                      alt=""
                      className="w-7 h-8 object-cover border-2 border-[#111] shrink-0"
                    />
                  )}
                  <span className="flex-1 min-w-0 font-display uppercase tracking-tight text-xs leading-tight truncate">
                    {b.name}
                  </span>
                  <span className="font-heading uppercase tracking-[0.18em] text-[10px] text-[var(--st-fg)]/60 shrink-0">
                    {b.units_sold} sold
                  </span>
                </li>
              ))}
            </ul>
          )}
        </motion.div>
      </div>
    </section>
  );
}


/* ─── Edit Order Modal ─────────────────────────────────────────────────────
 * Lets an admin correct a customer's shipping address / contact info before
 * retrying a failed Easyship shipment. Backend normalises country/state on
 * save (e.g. "USA" → "US", "Texas" → "TX") so the follow-up retry succeeds.
 * -------------------------------------------------------------------------- */
function EditOrderModal({ order, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [customer, setCustomer] = useState({
    first_name: order.customer?.first_name || "",
    last_name: order.customer?.last_name || "",
    email: order.customer?.email || "",
    phone: order.customer?.phone || "",
  });
  const [ship, setShip] = useState({
    line1: order.shipping_address?.line1 || "",
    line2: order.shipping_address?.line2 || "",
    city: order.shipping_address?.city || "",
    state: order.shipping_address?.state || "",
    postal_code: order.shipping_address?.postal_code || "",
    country: order.shipping_address?.country || "US",
  });

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const updated = await editOrder(order.id, {
        customer,
        shipping_address: ship,
      });
      onSaved(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/60 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="edit-order-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white border-2 border-[#111] shadow-brutal w-full max-w-2xl max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b-2 border-[#111]">
          <div className="font-heading uppercase tracking-[0.22em] text-sm">
            Edit order · {order.order_number}
          </div>
          <button
            onClick={onClose}
            data-testid="edit-order-close"
            className="border-2 border-[#111] p-1 hover:bg-[var(--st-mustard)]"
          >
            <X size={14} />
          </button>
        </div>

        <form onSubmit={onSave} className="p-5 space-y-5">
          <section>
            <h3 className="font-heading uppercase tracking-[0.22em] text-[11px] mb-2 text-[var(--st-fg)]/70">
              Customer
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <EditField label="First name" v={customer.first_name}
                on={(v) => setCustomer({ ...customer, first_name: v })}
                testid="edit-customer-first-name" />
              <EditField label="Last name" v={customer.last_name}
                on={(v) => setCustomer({ ...customer, last_name: v })}
                testid="edit-customer-last-name" />
              <EditField label="Email" v={customer.email} type="email"
                on={(v) => setCustomer({ ...customer, email: v })}
                testid="edit-customer-email" full />
              <EditField label="Phone" v={customer.phone}
                on={(v) => setCustomer({ ...customer, phone: v })}
                testid="edit-customer-phone" full />
            </div>
          </section>

          <section>
            <h3 className="font-heading uppercase tracking-[0.22em] text-[11px] mb-2 text-[var(--st-fg)]/70">
              Shipping address
            </h3>
            <p className="text-[11px] leading-relaxed text-[var(--st-fg)]/60 mb-3">
              Country/state auto-normalise on save (e.g. <code>United States → US</code>,{" "}
              <code>Texas → TX</code>). That&apos;s expected — Easyship only accepts ISO codes.
              Once saved, click <strong>Retry Shipment</strong> back on the order card.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <EditField label="Address line 1" v={ship.line1}
                on={(v) => setShip({ ...ship, line1: v })}
                testid="edit-ship-line1" full />
              <EditField label="Address line 2" v={ship.line2}
                on={(v) => setShip({ ...ship, line2: v })}
                testid="edit-ship-line2" full />
              <EditField label="City" v={ship.city}
                on={(v) => setShip({ ...ship, city: v })}
                testid="edit-ship-city" />
              <EditField label="State"
                hint="TX or Texas (auto-normalised)"
                v={ship.state}
                on={(v) => setShip({ ...ship, state: v })}
                testid="edit-ship-state" />
              <EditField label="Postal code" v={ship.postal_code}
                on={(v) => setShip({ ...ship, postal_code: v })}
                testid="edit-ship-postal" />
              <EditField label="Country"
                hint="US or USA (auto-normalised to ISO alpha-2)"
                v={ship.country}
                on={(v) => setShip({ ...ship, country: v })}
                testid="edit-ship-country" />
            </div>
          </section>

          <div className="flex items-center justify-end gap-2 pt-2 border-t-2 border-[#11111122]">
            <button
              type="button"
              onClick={onClose}
              className="font-heading uppercase tracking-[0.18em] text-xs px-4 py-2 border-2 border-[#111] hover:bg-[var(--st-cream)]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="edit-order-save"
              className="font-heading uppercase tracking-[0.18em] text-xs px-4 py-2 border-2 border-[#111] bg-[var(--st-gold)] hover:bg-[var(--st-mustard)] shadow-brutal-sm disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save & normalise"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditField({ label, hint, v, on, type = "text", testid, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="block text-[10px] font-heading uppercase tracking-[0.22em] text-[var(--st-fg)]/60 mb-1">
        {label}
      </label>
      <input
        type={type}
        value={v}
        onChange={(e) => on(e.target.value)}
        data-testid={testid}
        className="w-full border-2 border-[#111] bg-[var(--st-cream)] px-3 py-2 font-body text-sm focus:outline-none focus:bg-white"
      />
      {hint && (
        <p className="text-[10px] text-[var(--st-fg)]/50 mt-0.5 font-body">
          {hint}
        </p>
      )}
    </div>
  );
}

